import React from 'react'
import type { DropzoneOptions } from 'react-dropzone'
import { useDropzone } from 'react-dropzone'

import { CheckCircle, CloudUpload } from '../icons'
import { IndeterminateSpinner } from '../spinner'
import { classes } from '../utils'

export type FileUploaderProps = {
  prompt?: string | React.ReactElement
  subPrompt?: string | React.ReactElement
  icon?: 'cloud' | 'fileAdded' | React.ReactElement
  className?: string
  isPending?: boolean
} & Pick<Required<DropzoneOptions>, 'onDrop'> &
  Omit<DropzoneOptions, 'onDrop'>

export default function FileUploader({
  prompt,
  subPrompt,
  icon = 'cloud',
  onDrop,
  disabled,
  className,
  isPending,
  ...dropOptions
}: FileUploaderProps) {
  const { getRootProps, getInputProps, isDragActive, isFileDialogActive } =
    useDropzone({
      onDrop,
      disabled,
      ...dropOptions,
    })

  const renderPrompt = () => {
    if (isDragActive) {
      return (
        <p className="mb-2 text-sm text-foreground-subtle">
          Drop your file here
        </p>
      )
    } else if (typeof prompt === 'string') {
      return <p className="mb-2 text-sm text-foreground-subtle">{prompt}</p>
    } else if (!!prompt) {
      return prompt
    }

    return (
      <p className="mb-2 text-foreground-subtle">
        <span className="font-semibold">Click to upload</span> or drag and drop
      </p>
    )
  }

  const renderSubPrompt = () => {
    if (typeof subPrompt === 'string') {
      return <p className="text-xs text-foreground-subtle">{subPrompt}</p>
    } else if (!!subPrompt) {
      return subPrompt
    }

    return null
  }

  const renderIcon = () => {
    if (!!icon && typeof icon !== 'string') {
      return icon
    } else if (icon === 'cloud') {
      return <CloudUpload className="mb-3 h-10 w-10 text-foreground-subtle" />
    } else if (icon === 'fileAdded') {
      return <CheckCircle className="mb-3 h-10 w-10 text-foreground-subtle" />
    }

    return null
  }

  return (
    <div
      {...getRootProps()}
      className={classes(
        'flex h-64 w-full items-center justify-between rounded-lg border border-border-normal px-2 py-1.5 outline-none',
        {
          'hover:bg-background-surface/70': !disabled && !isPending,
        },
        {
          'border-border-normal bg-background-surface/70':
            isDragActive && !disabled && !isPending,
        },
        {
          'outline outline-2 outline-offset-[-2px] outline-focus-outline':
            isFileDialogActive,
        },
        disabled || isPending
          ? 'cursor-not-allowed'
          : 'cursor-pointer hover:bg-background-surface/70',
        className,
      )}
      data-testid="dragZone"
    >
      <label
        htmlFor="file-upload"
        className={classes(
          'relative flex flex-1 items-center justify-center',
          disabled || isPending ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
        aria-disabled={disabled}
      >
        <div className="relative flex flex-col items-center justify-center pb-6 pt-5">
          {renderIcon()}
          {renderPrompt()}
          {renderSubPrompt()}
          <input id="file-upload" {...getInputProps()} className="hidden" />
        </div>
        {isPending && (
          <IndeterminateSpinner className="absolute bottom-2 right-2 h-6 w-6 text-foreground-subtle" />
        )}
      </label>
    </div>
  )
}
